<script setup lang="ts">
import { debugConsoleError, debugConsoleWarn } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { computed, ref } from 'vue'
import {
  type ChatItem,
  type GroupedReaction,
  type MessageWithReply,
  formatMessageSenderShortName,
  getAvatarColor,
  getInitials,
} from '../../chatModules'
import AuthenticatedVImg from '../AuthenticatedVImg.vue'
import Message from './Message.vue'
import { isGroupLikeChatType } from '@/modules/chat-matrix-fast/constants/chat'
import { findViewAvatarByMatrixUserId } from '@/modules/chat-matrix-fast/stores/chatStoreSync'
import type { ChatType } from '@/modules/chat-matrix-fast/types/IChat'

interface Props {
  item: Extract<ChatItem, { kind: 'group' }>
  isMine: (senderId: number | string) => boolean
  isPinned?: (eventId: string | null | undefined) => boolean
  contact: {
    id?: number | string
    avatar?: string
    fullName?: string
  }
  profileUser: {
    id?: number | string
    fullName?: string
    avatar?: string
  } | null
  hoveredMessageId: string | number | null
  getMessageId: (msgData: MessageWithReply, senderId: number | string) => string | number
  getGroupedReactions: (msgData: MessageWithReply) => GroupedReaction[]
  getReplySenderName: (msgData: MessageWithReply, senderId: number | string) => string
  chatId?: number | string
  chatType?: ChatType
  isLastMessageInChat?: boolean
  canPin?: boolean
  canForward?: boolean
  canReply?: boolean
  canOpenProfile?: boolean
  selectionMode?: boolean
  isMessageSelected?: (message: MessageWithReply) => boolean
  canSelectMessage?: (message: MessageWithReply) => boolean
}

interface Emits {
  (e: 'message-enter', messageId: string | number): void
  (e: 'message-leave', messageId: string | number): void
  (e: 'reaction-click', messageId: string | number, reactionTypeId: string | number, isMyReaction: boolean): void
  (e: 'trigger-enter', messageId: string | number, event: MouseEvent): void
  (e: 'trigger-leave', messageId: string | number): void
  (e: 'trigger-click', messageId: string | number, event: MouseEvent): void
  (e: 'menu-click', messageId: string | number, event: MouseEvent): void
  (e: 'quote-click', messageId: string | number, matrixEventId?: string): void
  (e: 'avatar-click', userId: string | number): void
  (e: 'toggle-select', message: MessageWithReply): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const failedAvatarKeys = ref<Record<string, true>>({})

function markAvatarFailed(key: string) {
  if (!failedAvatarKeys.value[key]) {
    failedAvatarKeys.value = { ...failedAvatarKeys.value, [key]: true }
  }
}

function canShowAvatar(src: string | null | undefined, key: string) {
  return Boolean(src) && !failedAvatarKeys.value[key]
}

const avatarColor = computed(() => {
  return getAvatarColor(avatarDisplayName.value)
})

const isGroupChat = computed(() => {
  return isGroupLikeChatType(props.chatType) || props.chatType === 'event'
})

const avatarDisplayName = computed(() => {
  if (props.isMine(props.item.group.senderId)) {
    return props.profileUser?.fullName || 'Я'
  }

  const firstMessage = props.item.group.messages[0]
  return getMessageAuthorName(firstMessage) || props.contact?.fullName || 'К'
})

const avatarSrc = computed(() => {
  if (props.isMine(props.item.group.senderId)) {
    return resolveOwnAvatar(props.item.group.messages[0])
  }

  const firstMessage = props.item.group.messages[0]
  return getMessageAvatar(firstMessage) || (isGroupChat.value ? null : props.contact.avatar)
})

const avatarInitials = computed(() => getInitials(avatarDisplayName.value))

const avatarFailKey = computed(() => `sender-${props.item.group.senderId}`)

const resolveMessageSenderMxid = (msg: MessageWithReply): string | null => {
  const msgAny = msg as any
  const candidates = [
    msgAny.author_id,
    msg.created_by?.id,
    msgAny.created_by?.id,
    msgAny.author,
    props.item.group.senderId,
  ]

  for (const value of candidates) {
    const mxid = String(value || '').trim()
    if (mxid.startsWith('@')) {
      return mxid
    }
  }

  return null
}

const isMatrixOrBlobAvatar = (url: string | null | undefined) => {
  if (!url) return false
  return url.startsWith('mxc://')
    || url.startsWith('mxc:')
    || url.startsWith('blob:')
    || url.includes('/_matrix/media/')
    || url.includes('/_matrix/client/v1/media/')
}

// Список слева: S3. В пузыре часто mxc→blob → ERR_FILE_NOT_FOUND. S3 из контактов приоритетнее.
const getMessageAvatar = (msg: MessageWithReply) => {
  const msgAny = msg as any
  const mxid = resolveMessageSenderMxid(msg)
  const viewAvatar = mxid ? findViewAvatarByMatrixUserId(mxid) : null
  if (viewAvatar && !isMatrixOrBlobAvatar(viewAvatar)) {
    return viewAvatar
  }

  const candidates = [
    msgAny.avatar,
    msg.created_by?.avatar,
    msgAny.created_by?.avatar,
  ]

  for (const candidate of candidates) {
    const url = typeof candidate === 'string' ? candidate.trim() : ''
    if (url && !isMatrixOrBlobAvatar(url)) {
      return url
    }
  }

  for (const candidate of candidates) {
    const url = typeof candidate === 'string' ? candidate.trim() : ''
    if (url) {
      return url
    }
  }

  return viewAvatar
}

/** Свои сообщения: IAM/profileUser приоритетнее Matrix enrichment (часто пустой). */
const resolveOwnAvatar = (msg?: MessageWithReply) => {
  return props.profileUser?.avatar
    || (msg ? getMessageAvatar(msg) : null)
    || null
}

const getMessageAuthorName = (msg: MessageWithReply) => {
  const msgAny = msg as any

  return msg.created_by?.full_name
    || msgAny.author_name
    || msgAny.name
    || (msg.created_by?.first_name && msg.created_by?.last_name
      ? `${msg.created_by.first_name} ${msg.created_by.last_name}`.trim()
      : null)
    || msg.created_by?.user_name
    || msg.created_by?.username
    || null
}

/** Имя в пузыре — только у первого чужого сообщения в группе (как в Telegram) */
const resolveSenderLabel = (msg: MessageWithReply, msgIndex: number) => {
  if (!isGroupChat.value || msgIndex !== 0 || props.isMine(props.item.group.senderId)) {
    return null
  }

  const shortName = formatMessageSenderShortName(msg)
  if (!shortName) return null

  return {
    name: shortName,
    colorClass: getAvatarColor(getMessageAuthorName(msg) || shortName),
  }
}

const hasAnyContent = computed(() => {
  return props.item.group.messages.some(msg => {
    return !!(
      msg.message
      || (msg.attachments && msg.attachments.length > 0)
      || msg.reply_to
    )
  })
})

const handleAvatarClick = (msg: MessageWithReply) => {
  let userId: string | number | null = null

  if (msg.created_by?.id) {
    userId = msg.created_by.id
  }

  if (!userId && msg.created_by) {
    const createdByAny = msg.created_by as any
    userId = createdByAny.uuid || createdByAny.user_id || createdByAny.user_uuid
  }

  if (!userId) {
    const msgAny = msg as any
    userId = msgAny.senderId || msgAny.sender_id || msgAny.user_id
  }

  if (!userId) {
    userId = props.item.group.senderId
  }

  if (userId && props.isMine(userId)) {
    return
  }

  if (userId) {
    emit('avatar-click', userId)
  } else {
    debugConsoleWarn('[MessageGroup][handleAvatarClick] Не удалось получить userId', {
      msg,
      createdBy: msg.created_by,
      groupSenderId: props.item.group.senderId,
    })
  }
}

const visibleMessages = computed(() => {
  return props.item.group.messages.filter(msg => !!(
    msg.message
    || (msg.attachments && msg.attachments.length > 0)
    || msg.reply_to
  ))
})

const visibleMessagesWithLabels = computed(() =>
  visibleMessages.value.map((msg, index) => ({
    msg,
    senderLabel: resolveSenderLabel(msg, index),
  })),
)
</script>

<template>
  <div
    v-if="hasAnyContent || visibleMessages.length > 0"
    class="chat-group d-flex align-start"
    :class="[
      {
        'flex-row-reverse': isMine(item.group.senderId),
      },
    ]"
  >
    <div
      class="chat-avatar"
      :class="isMine(item.group.senderId) ? 'ms-4' : 'me-4'"
    >
      <VAvatar
        size="32"
        class="cursor-pointer"
        :class="[!canShowAvatar(avatarSrc, avatarFailKey) ? avatarColor : '']"
        @click.stop="handleAvatarClick(item.group.messages[0])"
      >
        <AuthenticatedVImg
          v-if="canShowAvatar(avatarSrc, avatarFailKey)"
          :src="avatarSrc!"
          @error="markAvatarFailed(avatarFailKey)"
        />
        <span
          v-else
          class="text-sm"
        >{{ avatarInitials }}</span>
      </VAvatar>
    </div>
    <div
      class="chat-body d-inline-flex flex-column"
      :class="isMine(item.group.senderId) ? 'align-end' : 'align-start'"
    >
      <div
        v-for="({ msg: msgData, senderLabel }, msgIndex) in visibleMessagesWithLabels"
        :key="msgData.time || msgData.id"
        class="d-flex align-start"
        :class="[
          {
            'flex-row-reverse': isMine(item.group.senderId),
          },
        ]"
      >
        <Message
          :message="msgData"
          :message-id="getMessageId(msgData, item.group.senderId)"
          :is-mine="isMine(item.group.senderId)"
          :is-pinned="Boolean(isPinned?.(msgData.matrix_event_id))"
          :is-last-in-group="msgIndex === visibleMessagesWithLabels.length - 1"
          :is-last-in-chat="!!isLastMessageInChat && msgIndex === visibleMessagesWithLabels.length - 1"
          :is-hovered="hoveredMessageId === getMessageId(msgData, item.group.senderId)"
          :reactions="getGroupedReactions(msgData)"
          :reply-sender-name="getReplySenderName(msgData, item.group.senderId)"
          :sender-name="senderLabel?.name"
          :sender-name-color-class="senderLabel?.colorClass"
          :chat-id="chatId"
          :chat-type="chatType"
          :can-pin="canPin"
          :can-forward="canForward"
          :can-reply="canReply"
          :can-open-profile="canOpenProfile"
          :selection-mode="selectionMode"
          :is-selected="isMessageSelected?.(msgData) ?? false"
          :can-select="canSelectMessage?.(msgData) ?? false"
          @mouseenter="emit('message-enter', getMessageId(msgData, item.group.senderId))"
          @mouseleave="emit('message-leave', getMessageId(msgData, item.group.senderId))"
          @reaction-click="(reactionTypeId, isMyReaction) => emit('reaction-click', getMessageId(msgData, item.group.senderId), reactionTypeId, isMyReaction)"
          @trigger-enter="emit('trigger-enter', getMessageId(msgData, item.group.senderId), $event)"
          @trigger-leave="emit('trigger-leave', getMessageId(msgData, item.group.senderId))"
          @trigger-click="emit('trigger-click', getMessageId(msgData, item.group.senderId), $event)"
          @menu-click="emit('menu-click', getMessageId(msgData, item.group.senderId), $event)"
          @quote-click="(messageId, matrixEventId) => emit('quote-click', messageId, matrixEventId)"
          @toggle-select="emit('toggle-select', msgData)"
        />
      </div>
    </div>
  </div>
</template>
